import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import Footer from "../../components/Client/Footer";
import { useAuth } from "../../context/AuthContext";
import { getOrders } from "../../api/orderService";
import "../../components/Client/clientTheme.css";
import "./Orders.css";

const STATUS_LABEL_KEYS = {
  1: "statusPending",
  2: "statusConfirmed",
  3: "statusPreparing",
  4: "statusReadyForPickup",
  5: "statusOutForDelivery",
  6: "statusDelivered",
  7: "statusCancelled",
  8: "statusRefunded",
};

const STATUS_CLASSES = {
  1: "accent",
  2: "brand",
  3: "brand",
  4: "brand",
  5: "brand",
  6: "success",
  7: "danger",
  8: "danger",
};

const PAYMENT_METHOD_LABEL_KEYS = {
  1: "paymentMethodCash",
  2: "paymentMethodCard",
};

const ACTIVE_STATUSES = [1, 2, 3, 4, 5];

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function Orders() {
  const { t } = useTranslation(["orders", "common"]);
  const { isAuthenticated, openLoginModal } = useAuth();
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("active");

  const loadOrders = useCallback(() => {
    if (!isAuthenticated) return;
    setError(null);
    getOrders()
      .then(setOrders)
      .catch(() => setError(t("loadOrdersErrorMessage")));
  }, [isAuthenticated, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const visibleOrders = (orders || []).filter((item) =>
    activeTab === "active" ? ACTIVE_STATUSES.includes(item.status) : !ACTIVE_STATUSES.includes(item.status)
  );

  return (
    <div className="client-orders-page">
      <ClientHeader />

      <div className="client-orders-content">
        <h1 className="client-orders-title">{t("pageTitle")}</h1>

        {!isAuthenticated ? (
          <div className="client-orders-empty">
            <p>{t("loginPromptOrders")}</p>
            <button type="button" className="client-orders-login-button" onClick={openLoginModal}>
              {t("common:logIn")}
            </button>
          </div>
        ) : (
          <>
            <div className="client-orders-tabs">
              <button
                type="button"
                className={`client-orders-tab ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                {t("tabInProgress")}
              </button>
              <button
                type="button"
                className={`client-orders-tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                {t("tabDelivered")}
              </button>
            </div>

            {error ? (
              <div className="client-orders-empty">
                <p>{error}</p>
                <button type="button" className="client-orders-login-button" onClick={loadOrders}>
                  {t("common:tryAgain")}
                </button>
              </div>
            ) : orders === null ? (
              <div className="client-orders-empty">
                <p>{t("loadingOrdersMessage")}</p>
              </div>
            ) : visibleOrders.length === 0 ? (
              <div className="client-orders-empty">
                <p>
                  {activeTab === "active"
                    ? t("noActiveOrdersMessage")
                    : t("noPastOrdersMessage")}
                </p>
              </div>
            ) : (
              <div className="client-orders-list">
                {visibleOrders.map((order) => (
                  <div className="client-order-card" key={order.orderId}>
                    <div className="client-order-top-row">
                      <div className="client-order-store">
                        {order.storeLogoUrl ? (
                          <img src={order.storeLogoUrl} alt="" className="client-order-store-logo" />
                        ) : (
                          <span className="client-order-store-logo client-order-store-logo-placeholder" />
                        )}
                        <span className="client-order-store-name">{order.storeName}</span>
                      </div>
                      <span className={`client-order-status-badge ${STATUS_CLASSES[order.status] || "accent"}`}>
                        {STATUS_LABEL_KEYS[order.status] ? t(STATUS_LABEL_KEYS[order.status]) : t("unknownLabel")}
                      </span>
                    </div>

                    <p className="client-order-meta">
                      {t("orderMetaLine", { orderNumber: order.orderNumber, date: formatDate(order.createdAt) })}
                      {order.paymentMethod != null
                        ? ` · ${
                            PAYMENT_METHOD_LABEL_KEYS[order.paymentMethod]
                              ? t(PAYMENT_METHOD_LABEL_KEYS[order.paymentMethod])
                              : t("unknownLabel")
                          }`
                        : ""}
                    </p>

                    <div className="client-order-items">
                      {order.items.map((line, index) => (
                        <div className="client-order-item-row" key={line.orderItemId ?? `${order.orderId}-${index}`}>
                          {line.productImageUrl ? (
                            <img src={line.productImageUrl} alt="" className="client-order-item-image" />
                          ) : (
                            <span className="client-order-item-image client-order-item-image-placeholder" />
                          )}
                          <span className={`client-order-item-text ${line.isRemoved ? "removed" : ""}`}>
                            {line.quantity}x {line.productName}
                            {line.variantName ? ` (${line.variantName})` : ""}
                            {line.isRemoved ? ` · ${t("removedItemLabel")}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>

                    {order.notes && <p className="client-order-notes">{t("orderNotePrefix", { notes: order.notes })}</p>}

                    <div className="client-order-summary-row">
                      <span>{t("subtotalLabel")}</span>
                      <span>{order.subTotal.toFixed(2)}</span>
                    </div>
                    <div className="client-order-summary-row">
                      <span>{t("deliveryLabel")}</span>
                      <span>{order.deliveryFee.toFixed(2)}</span>
                    </div>
                    <div className="client-order-total-row">
                      <span>{t("common:total")}</span>
                      <span>{order.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
